import { useState, useRef, useEffect } from 'react';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const FONT = "'Raleway', sans-serif";

export const STAGE_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#f97316',
  '#ef4444', '#8b5cf6', '#3b82f6', '#94a3b8',
];

const s = {
  // 2026-10-22: it wraps. On a phone the board's stage editor ran past its dialog (the whole dialog
  // scrolled sideways); the name keeps the first line and the rest wraps under it.
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 0.6rem', background: '#fff',
    border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 6,
    fontFamily: FONT,
  },
  handle: {
    display: 'flex', flexDirection: 'column', gap: 2, cursor: 'default',
    color: '#ccc', fontSize: '0.7rem', flexShrink: 0, userSelect: 'none',
  },
  arrowBtn: (disabled) => ({
    background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
    color: disabled ? '#e0e0e0' : '#94a3b8', fontSize: '0.9rem', padding: 0,
    lineHeight: 1, minWidth: 24, minHeight: 24, textAlign: 'center',   // a 24px target, the minimum to tap
  }),
  swatch: (colour) => ({
    width: 20, height: 20, borderRadius: '50%', background: colour,
    border: '2px solid rgba(0,0,0,0.1)', cursor: 'pointer', flexShrink: 0,
  }),
  nameInput: {
    fontFamily: FONT, fontSize: '0.86rem', padding: '0.3rem 0.4rem',
    border: 'none', borderBottom: `1.5px solid transparent`, borderRadius: 0,
    color: NAVY, outline: 'none', flex: '1 1 8rem', minWidth: 0, background: 'transparent',
    transition: 'border-color 0.15s',
  },
  badge: (active, colour) => ({
    padding: '0.15rem 0.5rem', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700,
    cursor: 'pointer', border: `1.5px solid ${active ? colour : BORDER}`,
    background: active ? colour + '20' : 'transparent',
    color: active ? colour : '#aaa', whiteSpace: 'nowrap', userSelect: 'none',
    transition: 'all 0.15s',
  }),
  actionsBadge: (count) => ({
    padding: '0.15rem 0.5rem', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700,
    cursor: 'pointer', border: `1.5px solid ${count > 0 ? '#6366f1' : BORDER}`,
    background: count > 0 ? '#eef2ff' : 'transparent',
    color: count > 0 ? '#6366f1' : '#aaa', whiteSpace: 'nowrap', userSelect: 'none',
  }),
  deleteBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
    fontSize: '1rem', padding: '0.2rem', flexShrink: 0,
  },
  disabledDelete: {
    background: 'none', border: 'none', color: '#e0e0e0',
    fontSize: '1rem', padding: '0.2rem', flexShrink: 0, cursor: 'not-allowed',
  },
  picker: {
    position: 'absolute', zIndex: 'var(--z-popover)', background: '#fff',
    border: `1px solid ${BORDER}`, borderRadius: 10, padding: '0.5rem',
    display: 'flex', gap: 6, flexWrap: 'wrap', width: 128,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 4,
  },
  pickerSwatch: (colour, selected) => ({
    width: 22, height: 22, borderRadius: '50%', background: colour,
    cursor: 'pointer', border: selected ? `2.5px solid ${NAVY}` : '2.5px solid transparent',
    boxSizing: 'border-box', transition: 'border 0.1s',
  }),
};

export default function StageRow({
  stage, index, total, onMove, onUpdate, onDelete, onToggleActions,
  actionsCount = 0, showAutomations = true, readOnly = false, isOnlyStage = false, showOutcome = false,
}) {
  const [colour, setColour]       = useState(stage.colour  || '#94a3b8');
  const [name, setName]           = useState(stage.name    || '');
  const [isTerminal, setTerminal] = useState(stage.isTerminal ?? false);
  const [isStart, setStart]       = useState(stage.isStart   ?? false);
  // Decision 80: a CRM stage's outcome (open | won | lost), set here rather than read from its name.
  const [outcome, setOutcome]     = useState(stage.outcome || 'open');
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  // Sync isStart from parent when another stage claims it
  useEffect(() => { setStart(stage.isStart ?? false); }, [stage.isStart]);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    function handler(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  function update(patch) {
    onUpdate?.({ colour, name, isTerminal, isStart, ...(showOutcome ? { outcome } : {}), ...patch });
  }

  function pickColour(c) {
    setColour(c);
    setPickerOpen(false);
    update({ colour: c });
  }

  function toggleTerminal() {
    const v = !isTerminal;
    setTerminal(v);
    update({ isTerminal: v });
  }

  function pickOutcome(v) {
    setOutcome(v);
    // Won and lost are where a deal ends, so they are Done stages too.
    if (v !== 'open' && !isTerminal) { setTerminal(true); update({ outcome: v, isTerminal: true }); return; }
    update({ outcome: v });
  }

  function toggleStart() {
    const v = !isStart;
    setStart(v);
    update({ isStart: v });
  }

  return (
    <div style={s.row}>
      {/* Up / Down arrows (reorder) */}
      <div style={s.handle}>
        <button
          style={s.arrowBtn(index === 0 || readOnly)}
          disabled={index === 0 || readOnly}
          onClick={() => onMove?.(index, index - 1)}
          title="Move up"
        >▲</button>
        <button
          style={s.arrowBtn(index === total - 1 || readOnly)}
          disabled={index === total - 1 || readOnly}
          onClick={() => onMove?.(index, index + 1)}
          title="Move down"
        >▼</button>
      </div>

      {/* Colour swatch + picker */}
      <div style={{ position: 'relative', flexShrink: 0 }} ref={pickerRef}>
        <div
          style={s.swatch(colour)}
          onClick={() => !readOnly && setPickerOpen(o => !o)}
          title="Change colour"
        />
        {pickerOpen && (
          <div style={s.picker}>
            {STAGE_COLORS.map(c => (
              <div
                key={c}
                style={s.pickerSwatch(c, c === colour)}
                onClick={() => pickColour(c)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Stage name */}
      <input
        style={{
          ...s.nameInput,
          borderBottomColor: document.activeElement === document.querySelector(`[data-stage="${stage.id}"]`) ? NAVY : 'transparent',
        }}
        data-stage={stage.id}
        value={name}
        placeholder="Stage name"
        disabled={readOnly}
        onChange={e => setName(e.target.value)}
        onBlur={() => update({ name })}
        onFocus={e => { e.target.style.borderBottomColor = NAVY; }}
        onBlurCapture={e => { e.target.style.borderBottomColor = 'transparent'; }}
      />

      {/* Start badge */}
      <span
        style={s.badge(isStart, '#3b82f6')}
        onClick={() => !readOnly && toggleStart()}
        title="Mark as start stage"
      >Start</span>

      {/* Terminal badge */}
      <span
        style={s.badge(isTerminal, '#10b981')}
        onClick={() => !readOnly && toggleTerminal()}
        title="Mark as terminal (done) stage"
      >Done</span>

      {showOutcome && (
        <select aria-label={`Outcome of ${name || 'this stage'}`} value={outcome} disabled={readOnly}
          onChange={(e) => pickOutcome(e.target.value)} data-testid="stage-outcome"
          style={{ flexShrink: 0, fontSize: 'var(--text-small)', padding: '4px 6px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)' }}>
          <option value="open">Open</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
        </select>
      )}

      {/* Automations: a template's, a board's or a project's own stage (2026-10-22). A button, so a
          keyboard reaches it (it was a clickable span). */}
      {showAutomations && (
        <button
          type="button"
          style={{ ...s.actionsBadge(actionsCount), border: 'none', font: 'inherit' }}
          onClick={() => onToggleActions?.()}
          title="Stage automations"
          aria-label={`Automations for ${name || 'this stage'}${actionsCount > 0 ? `, ${actionsCount} rule${actionsCount !== 1 ? 's' : ''}` : ''}`}
        >
          {actionsCount > 0 ? `${actionsCount} rule${actionsCount !== 1 ? 's' : ''}` : 'Automate'}
        </button>
      )}

      {/* Delete */}
      {readOnly || isOnlyStage ? (
        <button style={s.disabledDelete} disabled title={isOnlyStage ? 'Need at least one stage' : ''}>🗑</button>
      ) : (
        <button style={s.deleteBtn} title="Delete stage" onClick={onDelete}>🗑</button>
      )}
    </div>
  );
}
